import { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionGroup,
  AccordionPanel,
  AccordionTrigger,
  Alert,
  Box,
  Button,
  ButtonLink,
  Card,
  CardBody,
  CardFooter,
  Flex,
  Link,
  Tag,
  TagGroup,
  Text,
} from '@backstage/ui';
import {
  DECISION_NODES,
  DECISION_RESULTS,
  NODE_ORDER,
  START_NODE_ID,
} from './decision-data';
import type { DecisionPathEntry } from './decision-data';

const WRAP_STYLE = { flexWrap: 'wrap' } as const;
const LIST_STYLE = { paddingLeft: 16, margin: 0 } as const;

export const DecisionGuide = () => {
  const [path, setPath] = useState<DecisionPathEntry[]>([]);
  const [userExpandedKeys, setUserExpandedKeys] = useState<Set<string>>(
    new Set(),
  );

  const { currentNodeId, resultId } = useMemo(() => {
    if (path.length === 0)
      return { currentNodeId: START_NODE_ID, resultId: null };
    const lastEntry = path.at(-1);
    if (!lastEntry) return { currentNodeId: START_NODE_ID, resultId: null };
    const node = DECISION_NODES[lastEntry.nodeId];
    const chosen = node?.options.find(o => o.label === lastEntry.choiceLabel);
    return {
      currentNodeId: chosen?.nextNodeId ?? null,
      resultId: chosen?.resultId ?? null,
    };
  }, [path]);

  const answeredNodeIds = useMemo(
    () => path.map(entry => entry.nodeId),
    [path],
  );

  const visibleNodeIds = useMemo(
    () =>
      NODE_ORDER.filter(
        nodeId => answeredNodeIds.includes(nodeId) || nodeId === currentNodeId,
      ),
    [answeredNodeIds, currentNodeId],
  );

  const expandedKeys = useMemo(() => {
    const keys = new Set<string>(userExpandedKeys);
    if (resultId) {
      keys.add('result');
    } else if (currentNodeId) {
      keys.add(currentNodeId);
    }
    return keys;
  }, [currentNodeId, resultId, userExpandedKeys]);

  const handleExpandedChange = useCallback(
    (keys: Set<string | number>) => {
      const autoKey = resultId ? 'result' : currentNodeId;
      const userKeys = new Set<string>();
      for (const key of keys) {
        const k = String(key);
        if (k !== autoKey) {
          userKeys.add(k);
        }
      }
      setUserExpandedKeys(userKeys);
    },
    [currentNodeId, resultId],
  );

  const handleChoice = useCallback(
    (nodeId: string, choiceLabel: string) => {
      const nodeIndex = answeredNodeIds.indexOf(nodeId);
      const basePath = nodeIndex >= 0 ? path.slice(0, nodeIndex) : path;
      setPath([...basePath, { nodeId, choiceLabel }]);
      setUserExpandedKeys(new Set());
    },
    [answeredNodeIds, path],
  );

  const reset = useCallback(() => {
    setPath([]);
    setUserExpandedKeys(new Set());
  }, []);

  const result = resultId ? DECISION_RESULTS[resultId] : null;

  return (
    <Flex direction="column" gap="3">
      <Alert
        status="info"
        icon
        title="Plugin or Template Decision Guide"
        description={
          <Flex direction="column" gap="2">
            <Text variant="body-medium">
              Not sure whether to build a Backstage plugin or a scaffolder
              template? Answer a few quick questions and get a recommendation
              tailored to your use case.
            </Text>
            <TagGroup>
              <Tag size="small">Personalised recommendation</Tag>
              <Tag size="small">Links to relevant docs</Tag>
            </TagGroup>
          </Flex>
        }
      />
      <AccordionGroup
        allowsMultiple
        expandedKeys={expandedKeys}
        onExpandedChange={handleExpandedChange}
      >
        {visibleNodeIds.map((nodeId, index) => {
          const node = DECISION_NODES[nodeId];
          const isAnswered = answeredNodeIds.includes(nodeId);
          const answer = path.find(entry => entry.nodeId === nodeId);
          const stepLabel = `Question ${index + 1}`;

          return (
            <Accordion key={nodeId} id={nodeId}>
              <AccordionTrigger
                title={`${stepLabel}: ${node.question}`}
                subtitle={isAnswered && answer ? answer.choiceLabel : undefined}
              />
              <AccordionPanel>
                <Flex direction="column" gap="3" mb="2">
                  <em>
                    <Text variant="body-small">{node.hint}</Text>
                  </em>
                  <Flex gap="2" style={WRAP_STYLE}>
                    {node.options.map(option => (
                      <Button
                        key={option.label}
                        variant={
                          answer?.choiceLabel === option.label
                            ? 'primary'
                            : 'secondary'
                        }
                        onClick={() => handleChoice(nodeId, option.label)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </Flex>
                </Flex>
              </AccordionPanel>
            </Accordion>
          );
        })}

        {result && (
          <Accordion id="result" aria-live="polite">
            <AccordionTrigger title="Recommendation" subtitle={result.title} />
            <AccordionPanel>
              <Flex direction="column" gap="3" mb="3">
                <Alert
                  status="success"
                  icon
                  title={result.title}
                  description={result.description}
                />

                <Card>
                  <CardBody>
                    <Flex direction="column" gap="2">
                      <Text variant="body-medium" weight="bold">
                        Key details
                      </Text>
                      <Box as="ul" style={LIST_STYLE}>
                        {result.details.map(detail => (
                          <li key={detail}>
                            <Text variant="body-medium">{detail}</Text>
                          </li>
                        ))}
                      </Box>
                    </Flex>
                  </CardBody>
                  {result.links.length > 0 && (
                    <CardFooter>
                      <Flex gap="3" style={WRAP_STYLE}>
                        {result.links.map(link => (
                          <Link
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link.label}
                          </Link>
                        ))}
                      </Flex>
                    </CardFooter>
                  )}
                </Card>

                <Flex gap="2" style={WRAP_STYLE}>
                  {result.cta && (
                    <ButtonLink
                      href={result.cta.url}
                      variant="primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {result.cta.label}
                    </ButtonLink>
                  )}
                  <Button variant="secondary" onClick={reset}>
                    Start over
                  </Button>
                </Flex>
              </Flex>
            </AccordionPanel>
          </Accordion>
        )}
      </AccordionGroup>
    </Flex>
  );
};
